import { useState } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { MegaTierHeaders } from './MegaTierHeaders';
import { MegaTierChecksModal } from './MegaTierChecksModal';
import ShareToXButton from '@/components/ShareToXButton';
import { megaTierShareText, MEGA_TIER_HASHTAGS } from './megaTierShareText';
import { MegaTierTenureBadge } from './MegaTierTenureBadge';
import NewsWatchStar from '@/components/NewsWatchStar';

const pct = (v, d = 1) => (typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(d)}%` : '—');
const num = (v, d = 1) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(d) : '—');
const cap = (v) => {
  if (!v) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
};

function Dots({ items, testId, onClick, label }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="inline-flex items-center gap-[3px] rounded-md border border-border px-1.5 py-1 hover:border-amber-500 hover:bg-amber-500/10 transition-colors duration-150"
      title={`Open ${label} breakdown`}
      aria-label={`Open ${label} breakdown`}
      data-testid={testId}
    >
      {items.map((s) => (
        <span
          key={s.key}
          className={`inline-block w-2 h-2 rounded-full ${s.pass === null || s.pass === undefined ? 'bg-muted-foreground/30' : s.pass ? 'bg-emerald-500' : 'bg-rose-500'}`}
        />
      ))}
    </button>
  );
}

export function MegaTierTable({ rows, onSelect }) {
  const [checks, setChecks] = useState(null);
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="mega-tier-empty">No stocks in this stage for the current view.</p>;
  }
  return (
    <>
    <MegaTierChecksModal row={checks?.row} kind={checks?.kind} onClose={() => setChecks(null)} onOpenStockModal={onSelect} />
    <Table data-testid="mega-tier-table">
      <TableHeader>
        <TableRow>
          <MegaTierHeaders order={[
            ['rank', ''], ['ticker', ''], ['price', 'text-right'], ['mcap', 'text-right'], ['roc', 'text-right'], ['ey', 'text-right'],
            ['combined', 'text-right'], ['graham', 'text-center'], ['fscore', 'text-center'], ['pe', 'text-right'], ['pb', 'text-right'],
            ['ncav', 'text-right'], ['sector', ''],
          ]} />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.ticker} className="cursor-pointer hover:bg-amber-500/5" onClick={() => onSelect(r.ticker)} data-testid={`mega-tier-row-${r.ticker}`}>
            <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
              <ShareToXButton
                ticker={r.ticker}
                text={megaTierShareText(r)}
                hashtags={MEGA_TIER_HASHTAGS}
              />
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1.5">
                <NewsWatchStar ticker={r.ticker} isDividend={r.pays_dividend} variant="table" />
                <span className="font-bold font-mono" data-testid={`mega-tier-ticker-${r.ticker}`}>{r.ticker}</span>
                <span className={`text-[9px] font-bold px-1 rounded ${r.pays_dividend ? 'bg-sky-500/15 text-sky-500' : 'bg-zinc-500/15 text-zinc-400'}`}>
                  {r.pays_dividend ? 'DIV' : 'NON-DIV'}
                </span>
                {r.special_only && <span className="text-[9px] font-bold px-1 rounded bg-amber-500/15 text-amber-500" title="Only one-time special payouts on record — no recurring dividend" data-testid={`mega-tier-special-${r.ticker}`}>SPECIAL</span>}
                {r.is_adr && <span className="text-[9px] font-bold px-1 rounded bg-violet-500/15 text-violet-400">ADR</span>}
                {r.stage3_pass && <MegaTierTenureBadge ticker={r.ticker} since={r.elite_since} />}
              </div>
              <div className="text-[11px] text-muted-foreground truncate max-w-[200px] pl-[22px]">{r.name}</div>
            </TableCell>
            <TableCell className="text-right font-mono text-xs whitespace-nowrap px-1.5">${num(r.price, 2)}</TableCell>
            <TableCell className="text-right font-mono text-xs whitespace-nowrap px-1.5">{cap(r.market_cap)}</TableCell>
            <TableCell className="text-right font-mono text-xs text-emerald-500 whitespace-nowrap px-1.5">{pct(r.roc)}</TableCell>
            <TableCell className="text-right font-mono text-xs text-emerald-500 whitespace-nowrap px-1.5">{pct(r.earnings_yield)}</TableCell>
            <TableCell className="text-right font-mono text-xs whitespace-nowrap px-1.5">{r.combined_rank ?? '—'} <span className="text-muted-foreground">(top {num(r.combined_pct, 0)}%)</span></TableCell>
            <TableCell className="text-center px-1.5">
              <div className="font-mono text-xs font-bold">{r.graham_score}/5</div>
              <Dots items={r.graham_checks || []} label="Graham" onClick={() => setChecks({ row: r, kind: 'graham' })} testId={`mega-tier-graham-dots-${r.ticker}`} />
            </TableCell>
            <TableCell className="text-center px-1.5">
              <div className="font-mono text-xs font-bold">{r.f_score}/9</div>
              <Dots items={r.f_signals || []} label="F-Score" onClick={() => setChecks({ row: r, kind: 'fscore' })} testId={`mega-tier-fscore-dots-${r.ticker}`} />
            </TableCell>
            <TableCell className="text-right font-mono text-xs whitespace-nowrap px-1.5">{num(r.pe)}</TableCell>
            <TableCell className="text-right font-mono text-xs whitespace-nowrap px-1.5">{num(r.pb, 2)}</TableCell>
            <TableCell className="text-right font-mono text-xs whitespace-nowrap px-1.5" data-testid={`mega-tier-ncav-${r.ticker}`}>
              {typeof r.ncav_per_share === 'number' ? `$${r.ncav_per_share.toFixed(2)}` : '—'}
              {r.net_net && (
                <span className="ml-1 text-[9px] font-bold px-1 rounded bg-emerald-500/15 text-emerald-500" title="Trades at or below ⅔ of net current asset value — Graham net-net" data-testid={`mega-tier-netnet-${r.ticker}`}>NET-NET</span>
              )}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap min-w-[165px] px-1.5">{r.sector || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </>
  );
}
